/**
 * LegacyNarrationMigration — Phase 7D Completion Patch.
 *
 * Cross-course admin surface listing every lesson whose narration was
 * generated on the pre-v2 engine (or is otherwise flagged for review).
 * Admins can:
 *   · Preview old visible script vs corrected spoken text
 *   · Approve rerender per lesson OR bulk (with explicit confirmation)
 *   · Skip / mark reviewed
 *   · Roll back to the pre-migration audio at any time
 *
 * Rerender never runs automatically — ElevenLabs credits are only
 * spent after explicit approval.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import KcisAdminShell from "./KcisAdminShell";

const PAL = {
  bg: "#0F0B14", panel: "#1B1523", border: "#2D2436",
  text: "#F4F1F8", muted: "#A79DB4", accent: "#B084E8",
};

export default function LegacyNarrationMigration() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [previews, setPreviews] = useState({}); // lesson_id -> {display, spoken}
  const [running, setRunning] = useState(false);
  const [runningByCourse, setRunningByCourse] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/kcis/admin/kape/legacy-narration-scan/all");
      setItems(data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load legacy narration list");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = (key) => {
    const next = new Set(selected);
    if (next.has(key)) next.delete(key); else next.add(key);
    setSelected(next);
  };
  const toggleAllInCourse = (courseId) => {
    const next = new Set(selected);
    const keys = items.filter((r) => r.course_id === courseId).map((r) => `${r.course_id}:${r.lesson_id}`);
    const allSelected = keys.every((k) => next.has(k));
    for (const k of keys) {
      if (allSelected) next.delete(k); else next.add(k);
    }
    setSelected(next);
  };

  const grouped = useMemo(() => {
    const byCourse = new Map();
    for (const r of items) {
      if (!byCourse.has(r.course_id)) {
        byCourse.set(r.course_id, { title: r.course_title, rows: [] });
      }
      byCourse.get(r.course_id).rows.push(r);
    }
    return Array.from(byCourse.entries());
  }, [items]);

  const previewCorrected = async (row) => {
    try {
      const { data } = await api.post(
        `/kcis/admin/kape/courses/${row.course_id}/lessons/${row.lesson_id}/narration-processed-text`,
        {}
      );
      setPreviews((p) => ({ ...p, [`${row.course_id}:${row.lesson_id}`]: data }));
    } catch (e) { toast.error(e?.response?.data?.detail || "Preview failed"); }
  };

  const markReviewed = async (row) => {
    try {
      await api.patch(
        `/kcis/admin/kape/courses/${row.course_id}/lessons/${row.lesson_id}/narration/mark-reviewed`
      );
      toast.success("Marked as reviewed (no change)");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };

  const rollback = async (row) => {
    if (!window.confirm(`Roll back "${row.lesson_title}" to the pre-migration audio? Current audio will be swapped out.`)) return;
    try {
      await api.post(
        `/kcis/admin/kape/courses/${row.course_id}/lessons/${row.lesson_id}/narration-rollback`
      );
      toast.success("Rolled back to previous audio");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Rollback failed"); }
  };

  const startRerender = async (courseId, lessonIds, { bulk = false } = {}) => {
    if (!lessonIds.length) return;
    const message = bulk
      ? `Re-render ${lessonIds.length} lesson(s) via ElevenLabs? This will spend credits.`
      : "Re-render this lesson via ElevenLabs? This will spend credits.";
    if (!window.confirm(message)) return;
    setRunningByCourse((s) => ({ ...s, [courseId]: true }));
    setRunning(true);
    try {
      const { data } = await api.post(
        `/kcis/admin/kape/generation-jobs/narration-migration?course_id=${courseId}`,
        { lesson_ids: lessonIds, confirm: true }
      );
      toast.success(`Migration queued · job ${data.job_id?.slice(0, 8)}`);
      await pollJob(data.job_id);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Migration failed");
    } finally {
      setRunningByCourse((s) => ({ ...s, [courseId]: false }));
      setRunning(false);
    }
  };

  const pollJob = async (jobId) => {
    for (let i = 0; i < 240; i++) {
      await new Promise((r) => setTimeout(r, 5000));
      try {
        const { data } = await api.get(`/kcis/admin/kape/generation-jobs/${jobId}`);
        const st = data?.job?.status;
        if (["completed", "failed", "cancelled", "stale"].includes(st)) return data.job;
      } catch { /* keep polling */ }
    }
  };

  const total = items.length;
  const needsReview = items.filter((r) => r.migration_status === "needs_review").length;

  return (
    <KcisAdminShell>
      <div className="p-6 space-y-6" data-testid="legacy-migration-page"
           style={{ background: PAL.bg, color: PAL.text, minHeight: "100vh" }}>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <div className="text-xs uppercase tracking-widest" style={{ color: PAL.muted }}>
              Phase 7D Completion Patch
            </div>
            <h1 className="text-2xl font-semibold">Legacy Narration Migration</h1>
            <div className="text-sm mt-1" style={{ color: PAL.muted }}>
              {total} lesson(s) scanned · {needsReview} need review · re-renders never run automatically
            </div>
          </div>
          <Link to="/kcis/admin/job-center" className="text-xs underline"
                style={{ color: PAL.accent }}
                data-testid="legacy-migration-job-center-link">
            View Job Center
          </Link>
        </div>

        {loading && <div className="text-sm" style={{ color: PAL.muted }}>Loading…</div>}
        {!loading && total === 0 && (
          <div className="rounded-2xl p-6 text-sm border"
               style={{ background: PAL.panel, borderColor: PAL.border, color: PAL.muted }}
               data-testid="legacy-migration-empty">
            No lessons currently flagged for migration. Every generated narration is on the v2 engine.
          </div>
        )}

        {grouped.map(([courseId, entry]) => {
          const keys = entry.rows.map((r) => `${courseId}:${r.lesson_id}`);
          const allSelected = keys.every((k) => selected.has(k));
          const selectedIds = entry.rows
            .filter((r) => selected.has(`${courseId}:${r.lesson_id}`))
            .map((r) => r.lesson_id);
          const busy = !!runningByCourse[courseId];

          return (
            <section key={courseId}
                     className="rounded-2xl border overflow-hidden"
                     style={{ background: PAL.panel, borderColor: PAL.border }}
                     data-testid={`legacy-migration-course-${courseId}`}>
              <header className="px-5 py-3 flex items-center justify-between flex-wrap gap-2"
                      style={{ borderBottom: `1px solid ${PAL.border}` }}>
                <div>
                  <div className="text-sm font-semibold">{entry.title || "Untitled course"}</div>
                  <div className="text-[11px] font-mono" style={{ color: PAL.muted }}>{courseId.slice(0, 8)}</div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <button type="button" onClick={() => toggleAllInCourse(courseId)}
                          className="text-[11px] rounded-full px-3 py-1 border"
                          style={{ borderColor: PAL.border, color: PAL.text }}
                          data-testid={`legacy-migration-toggle-all-${courseId}`}>
                    {allSelected ? "Deselect all" : "Select all"}
                  </button>
                  <button type="button" onClick={() => startRerender(courseId, selectedIds, { bulk: true })}
                          disabled={selectedIds.length < 1 || busy || running}
                          className="text-[11px] rounded-full px-3 py-1 disabled:opacity-40"
                          style={{ background: PAL.accent, color: "#0F0B14" }}
                          data-testid={`legacy-migration-bulk-${courseId}`}>
                    {busy ? "Migrating…" : `Rerender selected (${selectedIds.length})`}
                  </button>
                </div>
              </header>

              <div className="divide-y" style={{ borderColor: PAL.border }}>
                {entry.rows.map((row) => {
                  const key = `${courseId}:${row.lesson_id}`;
                  const pv = previews[key];
                  return (
                    <div key={row.lesson_id} className="p-4 flex flex-col gap-2"
                         data-testid={`legacy-migration-row-${row.lesson_id}`}>
                      <div className="flex items-center gap-3 flex-wrap">
                        <input type="checkbox"
                               checked={selected.has(key)}
                               onChange={() => toggle(key)}
                               data-testid={`legacy-migration-select-${row.lesson_id}`} />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium truncate">{row.lesson_title}</div>
                          <div className="text-[11px]" style={{ color: PAL.muted }}>
                            {row.engine_version || "unknown engine"} ·
                            generated {row.generated_at?.slice(0, 10) || "—"} ·
                            {row.missing_timing_map ? " no timing map · " : " timing map present · "}
                            status <b>{row.migration_status}</b>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <button type="button" onClick={() => previewCorrected(row)}
                                  className="text-[11px] rounded-full px-3 py-1 border"
                                  style={{ borderColor: PAL.border, color: PAL.accent }}
                                  data-testid={`legacy-migration-preview-${row.lesson_id}`}>
                            Preview corrected
                          </button>
                          <button type="button" onClick={() => startRerender(courseId, [row.lesson_id])}
                                  disabled={busy || running}
                                  className="text-[11px] rounded-full px-3 py-1 disabled:opacity-40"
                                  style={{ background: "#7C3AED", color: "white" }}
                                  data-testid={`legacy-migration-rerender-${row.lesson_id}`}>
                            Approve rerender
                          </button>
                          <button type="button" onClick={() => markReviewed(row)}
                                  className="text-[11px] rounded-full px-3 py-1 border"
                                  style={{ borderColor: PAL.border, color: PAL.text }}
                                  data-testid={`legacy-migration-skip-${row.lesson_id}`}>
                            Mark reviewed
                          </button>
                          {row.previous_cache_id && (
                            <button type="button" onClick={() => rollback(row)}
                                    className="text-[11px] rounded-full px-3 py-1 border"
                                    style={{ borderColor: PAL.border, color: "#F87171" }}
                                    data-testid={`legacy-migration-rollback-${row.lesson_id}`}>
                              Rollback
                            </button>
                          )}
                        </div>
                      </div>

                      {pv && (
                        <div className="mt-1 grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div className="rounded p-3 text-xs whitespace-pre-wrap"
                               style={{ background: "#0F0B14", border: `1px solid ${PAL.border}` }}
                               data-testid={`legacy-migration-display-${row.lesson_id}`}>
                            <div className="text-[10px] uppercase mb-1" style={{ color: PAL.muted }}>
                              Visible script (transcript / captions)
                            </div>
                            {pv.display_script || "(no assembled script)"}
                          </div>
                          <div className="rounded p-3 text-xs whitespace-pre-wrap"
                               style={{ background: "#0F0B14", border: `1px solid ${PAL.border}` }}
                               data-testid={`legacy-migration-spoken-${row.lesson_id}`}>
                            <div className="text-[10px] uppercase mb-1" style={{ color: PAL.accent }}>
                              Corrected spoken text (what ElevenLabs will say)
                            </div>
                            {pv.tts_text || "(nothing to speak)"}
                            {Array.isArray(pv.warnings) && pv.warnings.length > 0 && (
                              <div className="mt-2 text-[10px]" style={{ color: "#F59E0B" }}>
                                {pv.warnings.map((w, i) => <div key={i}>· {w}</div>)}
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>
          );
        })}
      </div>
    </KcisAdminShell>
  );
}
